import * as findConversationShapeUtil from 'owa-mail-store/lib/services/utils/findConversationShapeUtil';
import * as RowNotificationHandler from './rowNotificationHandler';
import * as trace from 'owa-trace';
import isItemView from 'mini-mail-list-store/lib/utils/isItemView';
import listViewStore from 'mini-mail-list-store/lib/store/Store';
import MailFolderTableQuery from 'mini-mail-list-store/lib/store/schema/MailFolderTableQuery';
import NotificationPayloadBase from 'owa-service/lib/contract/NotificationPayloadBase';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import RowNotificationPayload from 'owa-service/lib/contract/RowNotificationPayload';
import SubscriptionParameters from 'owa-service/lib/contract/SubscriptionParameters';
import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import { action, select } from 'satcheljs/lib/legacy';
import { getRowSortByProperty } from '../../utils/tables/getRowSortByProperty';
import { loadInitialTableViewOnFirstRun } from './helpers/loadInitialTableViewOnFirstRun';
import { MailListRowDataType } from 'mini-mail-list-store/lib/store/schema/MailListRowDataType';
import { TableQueryType } from 'mini-mail-list-store/lib/store/schema/TableQuery';
import {
    lazyGetChannelId,
    lazySubscribe,
    lazyUnsubscribe,
    NotificationSubscription,
} from 'owa-notification';

const tableIdToSubscriptionMap: { [id: string]: NotificationSubscription } = {};

interface HandleRowNotificationState {
    tableView: TableView;
}

export const handleRowNotification = select<HandleRowNotificationState>({
    tableView: (notification: NotificationPayloadBase) =>
        listViewStore.tableViews.get(notification.id),
})(
    action('handleRowNotification')(function handleRowNotifications(
        notification: NotificationPayloadBase,
        state?: HandleRowNotificationState
    ): void {
        const tableView: TableView = state.tableView;

        if (!tableView) {
            // if folder is deleted and we receive a notification, which can happen when
            // there are pending notifications for this folder, we don't handle them and just return
            trace.trace.info('Received row notification for a deleted folder');
            return;
        }

        if (!tableView.isInitialLoadComplete) {
            // Do not process the notifications until the initial table load is complete.
            // We do this because we subscribe to the table notifications first and then
            // issue a findConversation call.
            trace.trace.info('Ignore row notifications as table has not loaded');
            return;
        }

        let rowData: MailListRowDataType;

        switch (notification.EventType) {
            case 'RowAdded':
                rowData = getRowDataFromRowNotification(
                    notification as RowNotificationPayload,
                    tableView
                );
                RowNotificationHandler.onAddRow(rowData, tableView);
                break;
            case 'RowModified':
                rowData = getRowDataFromRowNotification(
                    notification as RowNotificationPayload,
                    tableView
                );
                RowNotificationHandler.onUpdateRow(rowData, tableView);
                break;
            case 'RowDeleted':
                rowData = getRowDataFromRowNotification(
                    notification as RowNotificationPayload,
                    tableView
                );
                RowNotificationHandler.onDeleteRow(rowData, tableView);
                break;
            case 'QueryResultChanged':
            case 'Reload':
                RowNotificationHandler.onReload(tableView);
                break;
            default:
                // Ignore other type of notifications
                return;
        }
    })
);

function getRowDataFromRowNotification(
    notification: RowNotificationPayload,
    tableView: TableView
): MailListRowDataType {
    // Make sure the notification payload has the correct data type
    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            if (notification.Conversation) {
                return notification.Conversation;
            } else {
                trace.errorThatWillCauseAlert(
                    'Conversation should not be undefined or empty in notification payload.' +
                        notification.EventType +
                        ', ' +
                        notification.id
                );
                return null;
            }

        case ReactListViewType.Message:
            if (notification.Item) {
                return notification.Item;
            } else {
                trace.errorThatWillCauseAlert(
                    'Item should not be undefined or empty in notification payload.' +
                        notification.EventType +
                        ', ' +
                        notification.id
                );
                return null;
            }

        default:
            trace.errorThatWillCauseAlert(
                'rowNotificationStrategy: unsupported listViewType: ' +
                    tableView.tableQuery.listViewType
            );
            return null;
    }
}

export function unsubscribeToRowNotifications(tableId: string): void {
    const subscription: NotificationSubscription = tableIdToSubscriptionMap[tableId];
    if (subscription) {
        // unsubscribe this subscription
        lazyUnsubscribe.importAndExecute(subscription, handleRowNotification);
        delete tableIdToSubscriptionMap[tableId];
    }
}

export function subscribeToRowNotifications(tableView: TableView): void {
    if (tableIdToSubscriptionMap[tableView.id]) {
        // do not subscribe if we have existing subscription
        return;
    }

    if (tableView.tableQuery.type !== TableQueryType.Folder) {
        throw new Error('subscribeToRowNotifications should only be called for Folder table type');
    }

    const mailFolderTableQuery = tableView.tableQuery as MailFolderTableQuery;

    lazyGetChannelId.import().then(getChannelId => {
        const subscriptionParameters: SubscriptionParameters = {
            NotificationType: 'RowNotification',
            FolderId: getFolderIdForSubscription(tableView),
            FocusedViewFilter: mailFolderTableQuery.focusedViewFilter, // VSO 13477: Directly pass tableQuery to table loading methods
            IsConversation: !isItemView(tableView),
            Filter: mailFolderTableQuery.viewFilter,
            ChannelId: getChannelId(),
            MailboxId: null,
        };

        if (subscriptionParameters.IsConversation) {
            subscriptionParameters.ConversationShapeName = findConversationShapeUtil.getFindConversationShape(
                tableView.tableQuery.folderId
            );

            subscriptionParameters.ConversationResponseShape = findConversationShapeUtil.getFindConversationResponseShape();
            subscriptionParameters.SortBy = getRowSortByProperty(mailFolderTableQuery);
        } else {
            subscriptionParameters.SortBy = getRowSortByProperty(
                mailFolderTableQuery,
                false /* isConversation */
            );
        }

        const subscription: NotificationSubscription = {
            subscriptionId: tableView.id,
            requiresExplicitSubscribe: true,
            subscriptionParameters: subscriptionParameters,
        };

        /**
         * This part fetches the mails from the server before subscribing to SSE.
         * Ensures that mails received between startup-data fetch from the server and subscribing to SSE are not missed.
         */
        loadInitialTableViewOnFirstRun();

        // subscribe and store the subscription object
        lazySubscribe.importAndExecute(subscription, handleRowNotification);
        tableIdToSubscriptionMap[tableView.id] = subscription;
    });
}

function getFolderIdForSubscription(tableView: TableView): string {
    return tableView.tableQuery.type === TableQueryType.Folder
        ? tableView.tableQuery.folderId
        : null;
}
